import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog';
import { usePipe } from '@baseai/core/react';
import { ChatInput } from './chat-input';
import { ChatList } from './chat-list';
import { useEffect, useRef, useState } from 'react';
import { Suggestions } from './suggestions';
import { ChatMessage } from './chat-message';
import { Message } from '@baseai/core';
import { Separator } from '@/components/ui/separator';
import cn from 'mxcn';
import { useIsDarkMode } from './hooks/use-is-dark-mode';

export interface CustomStylesI {
	chatBtn?: {
		borderRadius?: string;
		backgroundColor?: string;
		foregroundColor?: string;
	};
	chatbot?: {
		borderRadius?: string;
		backgroundColor?: string;
		foregroundColor?: string;
	};
}

export function Chatbot({
	trigger,
	apiRoute,
	suggestions,
	openingMessage: openingContent,
	onConnect,
	onResponse,
	onError,
	onFinish,
	customStyles,
	title,
	triggerText,
	badge
}: {
	apiRoute: string;
	trigger?: React.ReactNode;
	openingMessage?: string;
	suggestions?: { title: string; prompt: string }[];
	onConnect?: (...args: any[]) => any;
	onResponse?: (...args: any[]) => any;
	onError?: (...args: any[]) => any;
	onFinish?: (...args: any[]) => any;
	customStyles?: CustomStylesI;
	title?: string;
	triggerText?: string;
	badge?: string;
}) {
	const [showLoadingMessage, setShowLoadingMessage] = useState(false);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const [darkMode, setDarkMode] = useState(true);
	const isDarkMode = useIsDarkMode();

	useEffect(() => {
		setDarkMode(isDarkMode);
	}, [isDarkMode]);

	function scrollToBottom() {
		const chatContainer = messagesContainerRef.current;
		if (chatContainer) {
			chatContainer.scrollTo({
				top: chatContainer.scrollHeight,
				behavior: 'smooth'
			});
		}
	}

	function getAskAITrigger() {
		if (trigger) {
			return (
				<div className={cn('lb-fixed lb-right-12 lb-bottom-12 lb-z-50')}>
					{trigger}
				</div>
			);
		}

		return (
			<div className={cn('lb-fixed lb-right-12 lb-bottom-12 lb-z-50')}>
				<Button
					className={cn(
						'lb-text-md lb-tracking-wide !lb-rounded-full lb-font-semibold'
					)}
					style={{
						...(customStyles?.chatBtn?.borderRadius && {
							borderRadius: customStyles?.chatBtn?.borderRadius
						}),
						...(customStyles?.chatBtn?.backgroundColor && {
							backgroundColor: !darkMode
								? customStyles?.chatBtn?.backgroundColor
								: customStyles?.chatBtn?.foregroundColor
						}),
						...(customStyles?.chatBtn?.foregroundColor && {
							color: !darkMode
								? customStyles?.chatBtn?.foregroundColor
								: customStyles?.chatBtn?.backgroundColor
						})
					}}
				>
					{triggerText ? triggerText : 'Ask AI'}
				</Button>
			</div>
		);
	}

	function handleRegenerateWithOptions() {
		regenerate({
			headers: { 'Custom-Header': 'Regenerate' },
			body: { customOption: 'regenerateValue' }
		});
	}

	function handleReset() {
		setMessages([]);
	}

	const {
		messages,
		input,
		handleInputChange,
		handleSubmit,
		isLoading,
		regenerate,
		setMessages,
		stop,
		sendMessage
	} = usePipe({
		apiRoute,
		stream: true,
		onConnect: () => {
			setShowLoadingMessage(true);
			onConnect?.();
		},
		onResponse: () => {
			scrollToBottom();
			setShowLoadingMessage(false);
			onResponse?.();
		},
		onError: () => {
			setShowLoadingMessage(false);
			onError?.();
		},
		onFinish: () => {
			onFinish?.();
		}
	});

	useEffect(() => {
		scrollToBottom();
	}, [messages.length]);

	const hasMessages = messages.length > 0;

	const sendSuggestedPrompt = async (prompt: string) => {
		await sendMessage(prompt);
	};

	const openingMessage: Message = {
		role: 'assistant',
		content:
			openingContent ??
			`Hey there!

I'm your AI assistant – managed and developed by Langbase.

Got a question? I'm here to help!`
	};

	const hasSuggestions = !!suggestions && suggestions.length > 0;

	return (
		<Dialog
			onOpenChange={open => {
				if (!open) setTimeout(handleReset, 300);
			}}
		>
			<DialogTrigger asChild>{getAskAITrigger()}</DialogTrigger>
			<DialogContent
				className="lb-flex lb-h-[80%] lb-max-h-[80vh] lb-w-[90%] lb-flex-col lb-border-border lb-outline-none sm:lb-h-[85%] sm:lb-max-h-[85vh] md:lb-max-w-[850px]"
				id="langbase-chatbot"
				style={{
					...(customStyles?.chatbot?.borderRadius && {
						borderRadius: customStyles?.chatbot?.borderRadius
					}),
					...(customStyles?.chatbot?.backgroundColor && {
						backgroundColor: !darkMode
							? customStyles?.chatbot?.backgroundColor
							: customStyles?.chatbot?.foregroundColor
					}),
					...(customStyles?.chatbot?.foregroundColor && {
						color: !darkMode
							? customStyles?.chatbot?.foregroundColor
							: customStyles?.chatbot?.backgroundColor
					}),
					...(customStyles?.chatbot?.foregroundColor && {
						borderColor: !darkMode
							? customStyles?.chatbot?.foregroundColor
							: customStyles?.chatbot?.backgroundColor
					})
				}}
			>
				<DialogHeader>
					<DialogTitle
						className="lb-text-foreground lb-flex lb-items-center"
						style={{
							...(customStyles?.chatbot?.backgroundColor && {
								color: !isDarkMode
									? customStyles?.chatbot?.foregroundColor
									: customStyles?.chatbot?.backgroundColor
							})
						}}
					>
						{title ? title : 'Ask AI'}

						{badge && (
							<span className="lb-ml-3 lb-px-2 lb-py-1 lb-text-[0.6rem] lb-tracking-wider lb-ring- lb-ring-muted-foreground lb-bg-muted-foreground/10 lb-rounded-full lb-text-primary/90 lb-font-medium">
								{badge}
							</span>
						)}
					</DialogTitle>
				</DialogHeader>
				<DialogDescription className="lb-h-full">
					<div
						className="lb-h-[65%] lb-overflow-auto sm:lb-h-[68%]"
						ref={messagesContainerRef}
					>
						{hasMessages ? (
							<ChatList
								messages={messages}
								isLoading={isLoading}
								customStyles={customStyles}
								showLoadingMessage={showLoadingMessage}
							/>
						) : (
							<>
								<div className="lb-mt-4 sm:lb-mt-8">
									<ChatMessage
										isLoading={isLoading}
										message={openingMessage}
										customStyles={customStyles}
										enableActions={false}
									/>
									{hasSuggestions && (
										<Separator
											className="lb-my-4 md:lb-my-8"
											style={{
												...(customStyles?.chatbot
													?.backgroundColor && {
													backgroundColor: !isDarkMode
														? customStyles?.chatbot
																?.foregroundColor
														: customStyles?.chatbot
																?.backgroundColor
												})
											}}
										/>
									)}
								</div>
								{hasSuggestions && (
									<Suggestions
										suggestions={suggestions}
										sendSuggestedPrompt={
											sendSuggestedPrompt
										}
										customStyles={customStyles}
									/>
								)}
							</>
						)}
					</div>
					<ChatInput
						stop={stop}
						input={input}
						reset={handleReset}
						messages={messages}
						isLoading={isLoading}
						onSubmit={handleSubmit}
						customStyles={customStyles}
						setInput={handleInputChange}
						regenerate={handleRegenerateWithOptions}
					/>
				</DialogDescription>
			</DialogContent>
		</Dialog>
	);
}

export default Chatbot;
